import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Trash2, Mail, Phone } from 'lucide-react';
import { getContacts, deleteContact } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';

export default function AdminMessages() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getContacts());
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load messages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteContact(id);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Contact Messages</h1>
      <p className="text-slate-500 text-sm mb-6">Messages submitted through the contact form</p>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-orange-500 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-400">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="message-card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900">{m.name}</h3>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {m.email}</span>
                    {m.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {m.phone}</span>}
                  </div>
                  <p className="text-slate-700 mt-3">{m.message}</p>
                  {m.created_at && <p className="text-xs text-slate-400 mt-2">{new Date(m.created_at).toLocaleString()}</p>}
                </div>
                <button onClick={() => handleDelete(m.id)} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid="delete-message-btn">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
